import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import {
    ChevronRight,
    Calendar,
    X,
    BookOpen,
    Loader2,
    Eye,
    Download,
    ArrowLeft,
    ChevronLeft
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

// Import react-pdf core features
import { Document, Page, pdfjs } from 'react-pdf';

// Grouped CSS imports here at the top to satisfy the ESLint rule
import 'react-pdf/dist/Page/TextLayer.css';
import 'react-pdf/dist/Page/AnnotationLayer.css';

// Now the executable assignment can safely run below the imports
pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

export default function Magazine() {
    const navigate = useNavigate();
    const [issues, setIssues] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [activeIssue, setActiveIssue] = useState(null);
    const [viewingPdf, setViewingPdf] = useState(false);

    // Mobile-responsive PDF pagination and scale tracking states
    const [numPages, setNumPages] = useState(null);
    const [pageNumber, setPageNumber] = useState(1);
    const [pdfScaleWidth, setPdfScaleWidth] = useState(350);
    const pdfContainerRef = useRef(null);

    const darkGreen = "#0B1A10";
    const goldAccent = "#D4AF37";
    const bgPattern = "https://www.transparenttextures.com/patterns/natural-paper.png";
    const fallbackImage = `https://placehold.co/400x533/${darkGreen.replace('#', '')}/${goldAccent.replace('#', '')}?text=Cover+Not+Found`;

    useEffect(() => {
        const fetchIssues = async () => {
            setIsLoading(true);
            try {
                const apiUrl = process.env.NODE_ENV === 'production'
                    ? '/api/magazines'
                    : 'http://localhost:5000/api/magazines';

                const res = await axios.get(apiUrl);

                // FIXED: Explicitly check that res.data is a valid array and not an HTML string/error object
                if (res.data && Array.isArray(res.data) && res.data.length > 0) {
                    setIssues(res.data);
                } else {
                    console.warn("API responded, but data was not an array. Using fallback mock data.");
                    setIssues(getMockData());
                }
            } catch (err) {
                console.error("Error fetching magazines, using fallback data:", err);
                setIssues(getMockData());
            } finally {
                setIsLoading(false);
            }
        };
        fetchIssues();
    }, []);

    // Responsive Layout Hook: tracks parent dimensions to cleanly auto-resize canvas pages
    useEffect(() => {
        if (viewingPdf && pdfContainerRef.current) {
            const handleResize = () => {
                if (pdfContainerRef.current) {
                    // Make it perfectly full width on mobile, leave visual padding on desktop
                    const isMobile = window.innerWidth < 768;
                    const padding = isMobile ? 0 : 24;
                    setPdfScaleWidth(pdfContainerRef.current.clientWidth - padding);
                }
            };

            // Set initial size
            handleResize();

            const debounceTimer = setTimeout(handleResize, 350);
            window.addEventListener('resize', handleResize);

            return () => {
                window.removeEventListener('resize', handleResize);
                clearTimeout(debounceTimer);
            };
        }
    }, [viewingPdf]);

    const handleCloseModal = () => {
        setActiveIssue(null);
        setPageNumber(1); // Reset page counter state
        setTimeout(() => setViewingPdf(false), 300);
    };

    const onDocumentLoadSuccess = ({ numPages }) => {
        setNumPages(numPages);
        setPageNumber(1);
    };

    if (isLoading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-[#FDFBF7]">
                <Loader2 className="animate-spin" size={48} style={{ color: goldAccent }} />
            </div>
        );
    }

    return (
        <div style={{ color: darkGreen }} className="min-h-screen bg-[#FDFBF7] relative overflow-hidden font-sans">
            <div className="absolute inset-0 pointer-events-none opacity-40 z-10" style={{ backgroundImage: `url(${bgPattern})` }}></div>

            <style dangerouslySetInnerHTML={{ __html: `
                .custom-scrollbar::-webkit-scrollbar { width: 6px; }
                .custom-scrollbar::-webkit-scrollbar-track { background: transparent; margin: 30px 0; }
                .custom-scrollbar::-webkit-scrollbar-thumb { background: ${goldAccent}; border-radius: 10px; }
                .custom-scrollbar::-webkit-scrollbar-thumb:hover { background: ${darkGreen}; }
                /* Hides extra invisible text selector layers causing shifting alignments inside mobile viewports */
                .react-pdf__Page__textLayer, .react-pdf__Page__annotationLayer { display: none !important; }
            `}} />

            <section className="relative h-[60vh] md:h-[65vh] flex items-center overflow-hidden">
                <div className="absolute inset-0 z-0">
                    <img
                        src="https://images.unsplash.com/photo-1499750310107-5fef28a66643?q=80&w=1600&auto=format&fit=crop"
                        className="w-full h-full object-cover"
                        alt="Magazine Hero"
                    />
                    <div className="absolute inset-0" style={{ backgroundColor: `${darkGreen}80` }}></div>
                </div>

                <div className="relative z-20 max-w-7xl mx-auto px-6 md:px-12 w-full pt-20">
                    <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.8 }}>
                        <div className="flex items-center gap-2 text-white/90 text-[10px] tracking-[0.4em] uppercase mb-6 font-bold">
                            <span>Publications</span>
                            <ChevronRight size={12} style={{ color: goldAccent }} />
                            <span className="text-white font-medium">Monthly Editions</span>
                        </div>
                        <h1 className="font-serif text-5xl md:text-8xl text-white leading-tight">
                            Our Latest <br/>
                            <span className="italic font-light" style={{ color: goldAccent }}>Monthly Releases</span>
                        </h1>
                    </motion.div>
                </div>
            </section>

            <section className="py-24 px-6 md:px-12 max-w-7xl mx-auto relative z-20">
                <div className="flex items-center gap-8 mb-16">
                    <h3 className="font-serif text-3xl whitespace-nowrap">Recent Magazine Releases</h3>
                    <div className="h-[1px] w-full bg-gray-200"></div>
                </div>

                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-16">
                    {/* FIXED: Protected with Array.isArray to guarantee it will never crash the UI layer */}
                    {Array.isArray(issues) && issues.map((issue, index) => (
                        <motion.article
                            key={issue.id}
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ delay: index * 0.1, duration: 0.6 }}
                            className="group bg-white p-6 rounded-[2.5rem] border border-gray-200/70 shadow-[0_4px_25px_-6px_rgba(0,0,0,0.03)] hover:shadow-xl transition-all duration-500 flex flex-col h-full gap-6"
                        >
                            <div className="overflow-hidden rounded-[1.8rem] aspect-[3/4] shadow-md relative bg-gray-50 cursor-pointer" onClick={() => setActiveIssue(issue)}>
                                <img
                                    src={issue.cover_image || fallbackImage}
                                    onError={(e) => { e.target.onerror = null; e.target.src = fallbackImage; }}
                                    className="w-full h-full object-cover group-hover:scale-103 transition-transform duration-700"
                                    alt={issue.title}
                                />
                                <div className="absolute top-4 left-4 bg-white/90 backdrop-blur-md px-4 py-1.5 rounded-full text-[9px] font-bold uppercase tracking-widest shadow-sm" style={{ color: goldAccent }}>
                                    Vol {issue.volume || index + 1}
                                </div>
                            </div>

                            <div className="space-y-4 flex-grow flex flex-col justify-between">
                                <div>
                                    <div className="flex items-center gap-4 text-[9px] uppercase tracking-widest font-bold opacity-60 mb-2">
                                        <span className="flex items-center gap-1.5">
                                            <Calendar size={12} style={{ color: goldAccent }}/> {issue.month} {issue.year}
                                        </span>
                                    </div>
                                    <h4 className="font-serif text-2xl group-hover:text-[#D4AF37] transition-colors cursor-pointer leading-tight mb-2" onClick={() => setActiveIssue(issue)}>
                                        {issue.title}
                                    </h4>
                                    <p className="text-gray-500 font-light text-xs leading-relaxed line-clamp-2 mb-4">
                                        {issue.description}
                                    </p>
                                </div>

                                <div className="flex items-center gap-3 pt-4 border-t border-gray-100 mt-auto">
                                    <button
                                        onClick={() => setActiveIssue(issue)}
                                        className="flex-1 py-3.5 rounded-xl border border-gray-200 text-gray-700 text-[10px] font-bold uppercase tracking-[0.2em] hover:border-[#D4AF37] hover:text-[#D4AF37] transition-all flex items-center justify-center gap-2 bg-gray-50/50 hover:bg-white"
                                    >
                                        <Eye size={14} style={{ color: goldAccent }} /> Preview
                                    </button>

                                    <a
                                        href={issue.pdf_url}
                                        download={`${issue.title.replace(/\s+/g, '_')}.pdf`}
                                        className="flex-1 flex items-center justify-center gap-2 py-3.5 rounded-xl text-[10px] font-bold uppercase tracking-[0.2em] transition-all shadow-md hover:opacity-90 active:scale-[0.99]"
                                        style={{ backgroundColor: darkGreen, color: goldAccent }}
                                    >
                                        <Download size={14} /> Get PDF
                                    </a>
                                </div>
                            </div>
                        </motion.article>
                    ))}
                </div>
            </section>

            <AnimatePresence>
                {activeIssue && (
                    <motion.div
                        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                        className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-md flex items-end md:items-center justify-center p-0 md:p-6"
                        onClick={handleCloseModal}
                    >
                        <motion.div
                            initial={{ y: "100%" }} animate={{ y: 0 }} exit={{ y: "100%" }}
                            className={`bg-[#FDFBF7] w-full max-w-5xl overflow-hidden relative shadow-2xl flex flex-col ${viewingPdf ? 'h-full md:h-[90vh] rounded-none md:rounded-[3rem]' : 'h-[95vh] md:h-[90vh] rounded-t-[3rem] md:rounded-[3rem]'}`}
                            onClick={e => e.stopPropagation()}
                        >
                            <button
                                onClick={handleCloseModal}
                                className={`absolute z-50 bg-white/90 backdrop-blur-md p-3 rounded-full hover:bg-gray-100 transition-all shadow-md ${viewingPdf ? 'hidden md:flex top-6 right-6' : 'top-4 right-4 md:top-6 md:right-6'}`}
                            >
                                <X size={20} style={{ color: darkGreen }}/>
                            </button>

                            {viewingPdf ? (
                                <div className="w-full h-full flex flex-col pt-3 md:pt-4 pb-6 md:pb-8 px-0 md:px-10">
                                    <div className="flex items-center justify-between mb-3 md:mb-6 px-4 md:px-0 md:pr-14">
                                        <button
                                            onClick={() => setViewingPdf(false)}
                                            className="flex items-center gap-1 md:gap-2 text-[10px] md:text-[11px] font-bold uppercase tracking-[0.1em] md:tracking-[0.2em] hover:opacity-70 transition-opacity"
                                        >
                                            <ArrowLeft size={16} style={{ color: goldAccent }} />
                                            <span className="hidden sm:inline">Back to Details</span>
                                            <span className="sm:hidden">Back</span>
                                        </button>

                                        <h3 className="font-serif text-xl hidden md:block">{activeIssue.title}</h3>

                                        <div className="flex items-center gap-3">
                                            <div className="text-[10px] md:text-xs font-semibold bg-gray-100 px-3 py-1.5 rounded-full">
                                                Page {pageNumber} of {numPages || "..."}
                                            </div>
                                            <button
                                                onClick={handleCloseModal}
                                                className="md:hidden bg-gray-100 p-1.5 rounded-full hover:bg-gray-200 transition-all flex items-center justify-center"
                                            >
                                                <X size={16} style={{ color: darkGreen }}/>
                                            </button>
                                        </div>
                                    </div>

                                    <div
                                        ref={pdfContainerRef}
                                        className="flex-grow w-full md:rounded-2xl overflow-y-auto overflow-x-hidden md:border border-gray-200 bg-[#1e221f] flex justify-center items-start p-0 md:p-2 relative shadow-inner custom-scrollbar"
                                    >
                                        <Document
                                            file={activeIssue.pdf_url}
                                            onLoadSuccess={onDocumentLoadSuccess}
                                            loading={
                                                <div className="flex items-center gap-3 text-white/70 py-20 mt-10">
                                                    <Loader2 className="animate-spin" size={20} style={{ color: goldAccent }} />
                                                    <span>Loading Pages...</span>
                                                </div>
                                            }
                                            error={
                                                <div className="flex flex-col items-center justify-center gap-3 text-white/70 py-20 mt-10 text-center px-4 w-full h-full">
                                                    <span className="text-red-400 font-bold text-lg">PDF Not Found</span>
                                                    <span className="text-sm">The document could not be loaded. Please verify the file path:</span>
                                                    <span className="text-xs font-mono bg-black/40 px-3 py-1.5 rounded text-white/50">{activeIssue.pdf_url}</span>
                                                </div>
                                            }
                                        >
                                            {numPages && (
                                                <Page
                                                    pageNumber={pageNumber}
                                                    width={pdfScaleWidth}
                                                    renderTextLayer={false}
                                                    renderAnnotationLayer={false}
                                                    className="md:shadow-2xl md:rounded-sm bg-white"
                                                />
                                            )}
                                        </Document>
                                    </div>

                                    <div className="flex items-center justify-between mt-4 max-w-xs mx-auto w-full bg-white border border-gray-200 rounded-full p-1 shadow-md">
                                        <button
                                            disabled={pageNumber <= 1}
                                            onClick={() => setPageNumber(prev => prev - 1)}
                                            className="p-2 rounded-full hover:bg-gray-100 transition-colors disabled:opacity-20 disabled:pointer-events-none"
                                            style={{ color: darkGreen }}
                                        >
                                            <ChevronLeft size={22} />
                                        </button>

                                        <span className="text-[10px] font-bold tracking-[0.2em] text-gray-400 uppercase select-none">
                                            Navigation
                                        </span>

                                        <button
                                            disabled={pageNumber >= numPages || !numPages}
                                            onClick={() => setPageNumber(prev => prev + 1)}
                                            className="p-2 rounded-full hover:bg-gray-100 transition-colors disabled:opacity-20 disabled:pointer-events-none"
                                            style={{ color: darkGreen }}
                                        >
                                            <ChevronRight size={22} />
                                        </button>
                                    </div>
                                </div>
                            ) : (
                                <div className="h-full overflow-y-auto custom-scrollbar flex flex-col md:flex-row relative">
                                    <div className="w-full md:w-5/12 h-[45vh] md:h-full relative sticky top-0">
                                        <img
                                            src={activeIssue.cover_image || fallbackImage}
                                            onError={(e) => { e.target.onerror = null; e.target.src = fallbackImage; }}
                                            className="w-full h-full object-cover"
                                            alt={activeIssue.title}
                                        />
                                        <div className="absolute inset-0 bg-gradient-to-t from-[#FDFBF7] via-transparent to-transparent md:bg-gradient-to-r"></div>
                                    </div>

                                    <div className="w-full md:w-7/12 px-8 pt-12 pb-6 md:p-16 relative z-10 bg-[#FDFBF7] -mt-12 md:mt-0 rounded-t-[3rem] md:rounded-none">
                                        <div className="flex items-center gap-4 text-[10px] font-bold uppercase tracking-[0.4em] mb-6 w-fit px-4 py-2 rounded-full border border-gray-200" style={{ color: goldAccent }}>
                                            <BookOpen size={14}/> <span>Vol {activeIssue.volume || "X"}</span>
                                        </div>

                                        <h2 className="font-serif text-4xl md:text-5xl leading-[1.1] mb-6">
                                            {activeIssue.title}
                                        </h2>

                                        <div className="flex flex-wrap gap-6 mb-10 text-gray-400 text-xs border-b border-gray-200 pb-8">
                                            <span className="flex items-center gap-2 font-bold uppercase tracking-widest">
                                                <Calendar size={14} style={{ color: goldAccent }}/> {activeIssue.month} {activeIssue.year}
                                            </span>
                                        </div>

                                        <div className="mb-12">
                                            <h3 className="font-serif text-2xl mb-6 flex items-center gap-3">
                                                Inside This Issue
                                            </h3>
                                            <div className="space-y-6">
                                                {activeIssue.articles?.map((article, idx) => (
                                                    <div key={idx} className="group flex items-start gap-5 cursor-pointer">
                                                        <span className="font-serif text-2xl opacity-20 group-hover:opacity-100 transition-opacity" style={{ color: goldAccent }}>
                                                            {(idx + 1).toString().padStart(2, '0')}
                                                        </span>
                                                        <div>
                                                            <h4 className="font-serif text-xl group-hover:text-[#D4AF37] transition-colors mb-2">
                                                                {article.title}
                                                            </h4>
                                                            <p className="text-[10px] font-bold uppercase tracking-[0.2em] opacity-50">
                                                                By {article.author}
                                                            </p>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>

                                        <div className="flex flex-col sm:flex-row gap-4 w-full">
                                            <button
                                                onClick={() => setViewingPdf(true)}
                                                className="flex-1 inline-flex items-center justify-center gap-3 py-5 rounded-full text-white font-bold uppercase tracking-[0.2em] text-[11px] transition-all hover:opacity-90 shadow-xl"
                                                style={{ backgroundColor: darkGreen }}
                                            >
                                                <Eye size={16} style={{ color: goldAccent }} /> Read Online
                                            </button>

                                            <a
                                                href={activeIssue.pdf_url}
                                                download={`${activeIssue.title.replace(/\s+/g, '_')}.pdf`}
                                                className="flex-1 inline-flex items-center justify-center gap-3 py-5 rounded-full bg-white border-2 border-gray-200 text-gray-800 font-bold uppercase tracking-[0.2em] text-[11px] transition-all hover:border-[#D4AF37]"
                                            >
                                                <Download size={16} style={{ color: darkGreen }} /> Download PDF
                                            </a>
                                        </div>

                                        <div className="h-20 md:h-4 w-full" />
                                    </div>
                                </div>
                            )}
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
}

function getMockData() {
    return [
        {
            id: 1,
            title: "Wellness Today",
            description: "Inaugural Issue: Ancient wisdom for modern wellness. Promoting holistic living through ancient wisdom, spiritual practices, and healing systems based on Sinhala indigenous medicine.",
            month: "July",
            year: "2026",
            volume: "1",
            cover_image: "/magazines/magazine-1-bg.png",
            pdf_url: "/magazines/Wellness_Today.pdf",
            articles: [
                { title: "Cover Story: Wellness Without Discrimination", author: "Boseth Wellness Foundation" },
                { title: "Don't Think Too Much: Five Tips to Calm the Mind", author: "Boseth Wellness Foundation" },
                { title: "Be Virtuous in Kali Yuga: Living with Purpose", author: "Boseth Wellness Foundation" },
                { title: "Wealth; Through a Wealthy Mind", author: "Boseth Wellness Foundation" },
                { title: "How to Preserve Your Luck", author: "Boseth Wellness Foundation" },
                { title: "Become Intelligent", author: "Boseth Wellness Foundation" },
                { title: "Srikantha Yoga in Vedic Astrology", author: "Boseth Wellness Foundation" },
                { title: "Brahmin Folk from Indian Literature", author: "Boseth Wellness Foundation" },
                { title: "Uplifting of Vibration", author: "Boseth Wellness Foundation" },
                { title: "Your Wellness Journey Begins Here", author: "Boseth Wellness Foundation" }
            ]
        },
        {
            id: 2,
            title: "Wellness Today",
            description: "August Issue: Exploring holistic living, martial arts traditions, and spiritual practices for modern wellness.",
            month: "August",
            year: "2026",
            volume: "2",
            cover_image: "/magazines/magazine-2-bg.png", // Changed to magazine-2-bg.png (fallback will catch it if missing)
            pdf_url: "/magazines/Wellness_Today_August.pdf", // Fixed typo from _Au.pdf
            articles: [
                { title: "Cover Story: Sri Lanka: World's No. 1 Trending Wellness Travel Destination", author: "Boseth Wellness Foundation" },
                { title: "Good Eastern Values", author: "Boseth Wellness Foundation" },
                { title: "Bodhidharma and the Chinese Tradition", author: "Boseth Wellness Foundation" },
                { title: "Initiation / Upanayana", author: "Boseth Wellness Foundation" },
                { title: "Lakshmi Related to Courage", author: "Boseth Wellness Foundation" },
                { title: "Three Things Essential Before Learning Art of Living", author: "Boseth Wellness Foundation" },
                { title: "Wellness Today", author: "Boseth Wellness Foundation" },
                { title: "References", author: "Boseth Wellness Foundation" }
            ]
        }
    ];
}